import { computed, inject, Injectable, signal } from '@angular/core';
import {
  deviceTypeTableIcon,
  HISTORY_DEFAULT_SEARCH_TYPE,
  HISTORY_SEARCH_TYPES
} from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.constants';
import {
  ListingDataSource,
  ListingPageModel
} from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { ListingTableComponent } from '../../../../../shared/components/listing-table/listing-table.component';
import { DeviceType, OrderDirection } from '../../../devices/components/devices.models';
import { historyLogTableSortField } from '../../history-log.constants';
import { DeviceHistoryFieldName, History } from '../../services/history-api.types';
import { HistoryDataService } from '../../services/history-data.service';
import { HistoryTableData } from '../../services/history-data.types';
import { HistoryValuesComponent } from '../history-values/history-values.component';

import { TableData } from 'src/app/shared/types/api-response.types';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { ListingPageModelAbstract } from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model-abstract';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { ListingPageId } from '@portal/shared/components/listing-page/store/listing-page-store.types';
import { ListingPageModelRegistry } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model-registry.service';
import { HistoryLogListingFiltersSourceService } from './history-log-listing-filters-adapter.service';
import { ExportCsvConfig } from 'src/app/services/file/export-data.service';
import { notAvailable } from '@portal/shared/constants';
import { CommDeviceIdColumnComponent } from '../../../devices/components/column/comm-device-id-column.component';
import { SerialNumberColumnComponent } from '../../../devices/components/column/serial-number-column.component';
import { devicesWithoutSerialNumber } from '../../../devices/constants/devices.constants';

@Injectable()
export class HistoryLogListingModelService extends ListingPageModelAbstract implements ListingPageModel {
  private readonly history = inject(HistoryDataService);
  private readonly tz = inject(TimezoneService);
  readonly listingPageModelRegistry = inject(ListingPageModelRegistry);

  readonly searchTypeList = computed(() => HISTORY_SEARCH_TYPES);
  readonly selectedSearchType = computed(() => HISTORY_DEFAULT_SEARCH_TYPE);
  readonly sorting = computed(() => ({
    columnName: DeviceHistoryFieldName.DateTimeUtcSet,
    orderDirection: OrderDirection.DESC
  }));
  readonly tableColumns = signal([
    {
      field: 'commDeviceId',
      title: TranslateRegistry.get.HISTORY_LOG.TABLE.COMM_DEVICE_ID,
      sortable: false,
      width: 200,
      cmp: CommDeviceIdColumnComponent
    },
    {
      field: 'serialNumber',
      title: TranslateRegistry.get.HISTORY_LOG.TABLE.SERIAL_NUMBER,
      sortable: false,
      width: 200,
      cmp: SerialNumberColumnComponent
    },
    {
      field: 'meterNumber',
      title: TranslateRegistry.get.HISTORY_LOG.TABLE.INVENTORY_ID,
      sortable: true,
      width: 200,
      text: (dataItem: History): string => `${dataItem.meterNumber ?? ''}`
    },
    {
      field: 'dateTimeUtcSet',
      title: TranslateRegistry.get.HISTORY_LOG.TABLE.DATE_TIME,
      width: 200,
      sortable: true,
      isRightAligned: true
    },
    {
      field: 'updateTypeName',
      title: TranslateRegistry.get.HISTORY_LOG.TABLE.UPDATE_TYPE,
      width: 200,
      sortable: false
    },
    {
      field: 'merged',
      title: TranslateRegistry.get.HISTORY_LOG.TABLE.UPDATED_VALUE,
      width: 400,
      minResizableWidth: 104,
      sortable: false,
      cmp: HistoryValuesComponent
    },
    {
      field: 'deviceTypeName',
      title: TranslateRegistry.get.HISTORY_LOG.TABLE.DEVICE_TYPE,
      width: 200,
      sortable: true,
      icon: deviceTypeTableIcon
    },
    { field: 'modelName', title: TranslateRegistry.get.HISTORY_LOG.TABLE.MODEL_NAME, width: 200, sortable: true },
    { field: 'updatedBy', title: TranslateRegistry.get.HISTORY_LOG.TABLE.UPDATED_BY, width: 200, sortable: true }
  ]);
  readonly dataSource: ListingDataSource<HistoryTableData> = {
    getAll: tableState => {
      return this.history.getTableData({
        ...tableState,
        filters: [
          ...(tableState.filters ?? []).map(f => ({
            ...f,
            filteredValues:
              f.filterType === DeviceHistoryFieldName.DateTimeUtcSet
                ? this.tz.getShiftedDatepickerRangeStringified({
                    start: new Date(f.filteredValues[0]),
                    end: new Date(f.filteredValues[1])
                  })
                : f.filteredValues
          }))
        ]
      });
    },
    total: 0
  };
  readonly exportCsv = {
    fileName: 'Export-HistoryLog',
    mapData: (data: TableData<History>): ExportCsvConfig[] => {
      return data.data.map(history => {
        const newValues = history.newValues ? JSON.parse(history.newValues) : null;
        const oldValues = history.oldValues ? JSON.parse(history.oldValues) : null;

        return {
          dateTimeUtcSet: { title: 'UpdatedDate/Time', value: history.dateTimeUtcSet },
          updateTypeName: { title: 'UpdateType', value: history.updateTypeName },
          merged: [
            {
              title: 'OldValue',
              value: oldValues ? this.history.prepareValuesForExport(oldValues, history.updateType) : notAvailable
            },
            {
              title: 'NewValue',
              value: newValues ? this.history.prepareValuesForExport(newValues, history.updateType) : notAvailable
            }
          ],
          deviceTypeName: { title: 'DeviceType', value: history.deviceTypeName ?? '' },
          modelName: { title: 'ModelName', value: history.modelName },
          commDeviceId: {
            title: 'CommDeviceID',
            value: history.commDeviceId ?? ''
          },
          serialNumber: {
            title: 'SerialNumber',
            value: devicesWithoutSerialNumber.includes(history.deviceType as DeviceType)
              ? ''
              : (history.serialNumber ?? 'Unknown Serial Number')
          },
          meterNumber: {
            title: 'InventoryID',
            value: history.meterNumber ?? ''
          },
          updatedBy: { title: 'UpdatedBy', value: history.updatedBy }
        };
      });
    }
  };
  readonly pageTitle = TranslateRegistry.get.HISTORY_LOG.TITLE.HISTORY_LOG;
  readonly tableCmp = ListingTableComponent;
  readonly sortField = historyLogTableSortField;
  readonly pageId = ListingPageId.HistoryLog;
  readonly filterSource = inject(HistoryLogListingFiltersSourceService);
  constructor() {
    super();
    const listingPageModelRegistry = this.listingPageModelRegistry;

    listingPageModelRegistry.add(this.pageId, this);
  }
}
